import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useAuth } from '../AuthContext';
import {
    Card, CardBody, Button, Input, Chip, Alert
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSpinner, faFilter, faFileArchive, faChevronDown, faChevronUp, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

const { rootUrl } = require('../../../config/config');

const customSelectStyles = {
    control: (base) => ({
        ...base,
        backgroundColor: '#f5f5f5',
        borderColor: '#d1d5db',
        '&:hover': {
            borderColor: '#9ca3af'
        }
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#f3f4f6' : 'white',
        color: state.isSelected ? 'white' : 'black',
        cursor: 'pointer'
    })
};

const ArchivedRemittances = () => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    // State
    const [owners, setOwners] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [weekFrom, setWeekFrom] = useState('');
    const [weekTo, setWeekTo] = useState('');
    const [archivedBy, setArchivedBy] = useState('');
    const [archivedData, setArchivedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});
    const [error, setError] = useState(null);
    const [, setSuccess] = useState(null);

    const statusOptions = [
        { value: 'PENDING', label: 'Pending' },
        { value: 'PROCESSED', label: 'Processed' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'PAID', label: 'Paid' }
    ];

    // Fetch initial data
    useEffect(() => {
        const initialize = async () => {
            await fetchOwners();
            // Set default to current year
            const currentYear = new Date().getFullYear();
            const defaultWeekFrom = `${currentYear}_01`;
            const defaultWeekTo = `${currentYear}_52`;
            setWeekFrom(defaultWeekFrom);
            setWeekTo(defaultWeekTo);
            // Auto-load current year data
            await loadData(null, null, defaultWeekFrom, defaultWeekTo, '');
        };
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchOwners = async () => {
        try {
            const response = await axios.get(`${rootUrl}/owners`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            setOwners(response.data || []);
        } catch (error) {
            console.error('Error fetching owners:', error);
        }
    };

    const loadData = async (owner, status, wkFrom, wkTo, archivedByFilter) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (owner) params.append('owner_code', owner.value);
            if (status) params.append('status', status.value);
            if (wkFrom) params.append('week_from', wkFrom);
            if (wkTo) params.append('week_to', wkTo);
            if (archivedByFilter) params.append('archived_by', archivedByFilter);

            const response = await axios.get(
                `${rootUrl}/api/remittance/archived?${params.toString()}`,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );

            if (response.data.success) {
                setArchivedData(response.data.data || []);
            } else {
                setError(response.data.message || 'Failed to fetch archived remittances');
                setArchivedData([]);
            }
        } catch (error) {
            console.error('Error fetching archived remittances:', error);
            setError(error.response?.data?.message || 'Failed to fetch archived remittances');
            setArchivedData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilters = async () => {
        await loadData(selectedOwner, selectedStatus, weekFrom, weekTo, archivedBy);
    };

    const handleClearFilters = async () => {
        setSelectedOwner(null);
        setSelectedStatus(null);
        setArchivedBy('');
        // Reset to current year
        const currentYear = new Date().getFullYear();
        const defaultWeekFrom = `${currentYear}_01`;
        const defaultWeekTo = `${currentYear}_52`;
        setWeekFrom(defaultWeekFrom);
        setWeekTo(defaultWeekTo);
        // Reload with current year data
        await loadData(null, null, defaultWeekFrom, defaultWeekTo, '');
    };

    const toggleRowExpansion = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0.00';
        return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        return new Date(timestamp).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            'PENDING': 'yellow',
            'PROCESSED': 'blue',
            'APPROVED': 'green',
            'PAID': 'green'
        };
        return colors[status] || 'gray';
    };

    const ownerOptions = owners.map(o => ({
        value: o.code,
        label: `${o.name} (${o.code})`
    }));

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Archived Remittances</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        View deleted remittance records and their archive history
                    </p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                {/* Filters Card */}
                <Card className="mb-6 shadow-xl">
                    <CardBody>
                        <div className="flex items-center gap-3 mb-4">
                            <FontAwesomeIcon icon={faFilter} className="text-[#FF5733]" size="lg" />
                            <h2 className="text-2xl font-bold text-[#2a2e30]">Filters</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                            {/* Owner Filter */}
                            <div>
                                <label className="block text-sm font-semibold text-[#2a2e30] mb-2">
                                    Owner
                                </label>
                                <Select
                                    value={selectedOwner}
                                    onChange={setSelectedOwner}
                                    options={ownerOptions}
                                    styles={customSelectStyles}
                                    isClearable
                                    placeholder="All Owners"
                                />
                            </div>

                            {/* Status Filter */}
                            <div>
                                <label className="block text-sm font-semibold text-[#2a2e30] mb-2">
                                    Status
                                </label>
                                <Select
                                    value={selectedStatus}
                                    onChange={setSelectedStatus}
                                    options={statusOptions}
                                    styles={customSelectStyles}
                                    isClearable
                                    placeholder="All Statuses"
                                />
                            </div>

                            {/* Archived By Filter */}
                            <div>
                                <label className="block text-sm font-semibold text-[#2a2e30] mb-2">
                                    Archived By
                                </label>
                                <Input
                                    type="text"
                                    value={archivedBy}
                                    onChange={(e) => setArchivedBy(e.target.value)}
                                    placeholder="Username"
                                    crossOrigin="anonymous"
                                />
                            </div>

                            {/* Week From */}
                            <div>
                                <label className="block text-sm font-semibold text-[#2a2e30] mb-2">
                                    Week From (YYYY_WW)
                                </label>
                                <Input
                                    type="text"
                                    value={weekFrom}
                                    onChange={(e) => setWeekFrom(e.target.value)}
                                    placeholder="2026_01"
                                    crossOrigin="anonymous"
                                />
                            </div>

                            {/* Week To */}
                            <div>
                                <label className="block text-sm font-semibold text-[#2a2e30] mb-2">
                                    Week To (YYYY_WW)
                                </label>
                                <Input
                                    type="text"
                                    value={weekTo}
                                    onChange={(e) => setWeekTo(e.target.value)}
                                    placeholder="2026_52"
                                    crossOrigin="anonymous"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end">
                            <Button
                                size="sm"
                                onClick={handleClearFilters}
                                className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm"
                            >
                                Clear Filters
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleApplyFilters}
                                className="bg-[#FF5733] hover:bg-[#e64520] text-white rounded-sm"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                        Loading...
                                    </>
                                ) : (
                                    'Apply Filters'
                                )}
                            </Button>
                        </div>
                    </CardBody>
                </Card>

                {/* Results Card */}
                <div className="mb-4">
                    {error && (
                        <Alert
                            color="red"
                            icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                            className="mb-4"
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}
                </div>

                <Card className="shadow-xl">
                    <CardBody>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <FontAwesomeIcon icon={faFileArchive} className="text-[#FF5733]" size="lg" />
                                <h2 className="text-2xl font-bold text-[#2a2e30]">Archived Records</h2>
                            </div>
                            <div className="text-sm text-gray-600">
                                Total: <span className="font-bold">{archivedData.length}</span> records
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-12">
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[#FF5733]" size="3x" />
                                <p className="text-gray-600 mt-4">Loading archived remittances...</p>
                            </div>
                        ) : archivedData.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                <FontAwesomeIcon icon={faFileArchive} size="3x" className="mb-4 opacity-50" />
                                <p className="text-lg font-semibold">No archived remittances found</p>
                                <p className="text-sm">Try adjusting your filters</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-max table-auto">
                                    <thead>
                                        <tr className="bg-gray-100 text-left">
                                            <th className="p-3 text-sm font-bold text-[#2a2e30]">Week</th>
                                            <th className="p-3 text-sm font-bold text-[#2a2e30]">Plate No</th>
                                            <th className="p-3 text-sm font-bold text-[#2a2e30]">Owner</th>
                                            <th className="p-3 text-sm font-bold text-[#2a2e30]">Status</th>
                                            <th className="p-3 text-sm font-bold text-[#2a2e30] text-right">Payout</th>
                                            <th className="p-3 text-sm font-bold text-[#2a2e30] text-right">Charges</th>
                                            <th className="p-3 text-sm font-bold text-[#2a2e30]">Archived Date</th>
                                            <th className="p-3 text-sm font-bold text-[#2a2e30]">Archived By</th>
                                            <th className="p-3 text-sm font-bold text-[#2a2e30]">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {archivedData.map((row) => (
                                            <React.Fragment key={row.id}>
                                                <tr className="border-b border-gray-200 hover:bg-gray-50">
                                                    <td className="p-3 text-sm">{row.week_key}</td>
                                                    <td className="p-3 text-sm font-semibold">{row.plate_no}</td>
                                                    <td className="p-3 text-sm">
                                                        <div>{row.owner_name}</div>
                                                        <div className="text-xs text-gray-500">{row.owner_code}</div>
                                                    </td>
                                                    <td className="p-3 text-sm">
                                                        <Chip
                                                            size="sm"
                                                            value={row.status}
                                                            color={getStatusColor(row.status)}
                                                        />
                                                    </td>
                                                    <td className="p-3 text-sm text-right font-semibold">
                                                        {formatCurrency(row.owner_payout)}
                                                    </td>
                                                    <td className="p-3 text-sm text-right">
                                                        {formatCurrency(row.total_charges)}
                                                    </td>
                                                    <td className="p-3 text-sm">
                                                        {formatTimestamp(row.archived_date)}
                                                    </td>
                                                    <td className="p-3 text-sm">{row.archived_by}</td>
                                                    <td className="p-3 text-sm">
                                                        <Button
                                                            size="sm"
                                                            onClick={() => toggleRowExpansion(row.id)}
                                                            className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm p-2"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={expandedRows[row.id] ? faChevronUp : faChevronDown}
                                                            />
                                                        </Button>
                                                    </td>
                                                </tr>
                                                {/* Expanded Details Row */}
                                                {expandedRows[row.id] && (
                                                    <tr className="bg-gray-50">
                                                        <td colSpan="9" className="p-4">
                                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                                                {/* Left Column - Remittance Details */}
                                                                <div className="space-y-3">
                                                                    <h4 className="font-bold text-[#2a2e30] mb-2">Remittance Details</h4>
                                                                    <div className="bg-white p-3 rounded space-y-2 text-sm">
                                                                        <div className="flex justify-between">
                                                                            <span className="text-gray-600">Vehicle:</span>
                                                                            <span className="font-semibold">
                                                                                {row.make} {row.model} ({row.year || 'N/A'})
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex justify-between">
                                                                            <span className="text-gray-600">Working Days:</span>
                                                                            <span className="font-semibold">{row.working_days || 6}</span>
                                                                        </div>
                                                                        <div className="flex justify-between">
                                                                            <span className="text-gray-600">Total Remittance:</span>
                                                                            <span className="font-semibold">{formatCurrency(row.total_remittance)}</span>
                                                                        </div>
                                                                        <div className="flex justify-between">
                                                                            <span className="text-gray-600">Net Income:</span>
                                                                            <span className="font-semibold">{formatCurrency(row.net_income)}</span>
                                                                        </div>
                                                                        <div className="flex justify-between border-t pt-2">
                                                                            <span className="text-gray-600">Processing Date:</span>
                                                                            <span className="font-semibold">{formatDate(row.processing_date)}</span>
                                                                        </div>
                                                                        <div className="flex justify-between">
                                                                            <span className="text-gray-600">Processed By:</span>
                                                                            <span className="font-semibold">{row.processed_by}</span>
                                                                        </div>
                                                                        {row.archive_reason && (
                                                                            <div className="flex flex-col border-t pt-2">
                                                                                <span className="text-gray-600 mb-1">Archive Reason:</span>
                                                                                <span className="text-sm italic text-gray-700">{row.archive_reason}</span>
                                                                            </div>
                                                                        )}
                                                                        {row.notes && (
                                                                            <div className="flex flex-col border-t pt-2">
                                                                                <span className="text-gray-600 mb-1">Notes:</span>
                                                                                <span className="text-sm italic text-gray-700">{row.notes}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Right Column - Applied Charges */}
                                                                <div className="space-y-3">
                                                                    <h4 className="font-bold text-[#2a2e30] mb-2">
                                                                        Applied Charges ({row.archived_charges?.length || 0})
                                                                    </h4>
                                                                    {row.archived_charges && row.archived_charges.length > 0 ? (
                                                                        <div className="bg-white p-3 rounded space-y-2">
                                                                            {row.archived_charges.map((charge, idx) => (
                                                                                <div
                                                                                    key={idx}
                                                                                    className={`p-2 rounded ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                                                                                >
                                                                                    <div className="flex justify-between items-start mb-1">
                                                                                        <Chip
                                                                                            size="sm"
                                                                                            value={charge.charge_type}
                                                                                            color="gray"
                                                                                            className="text-xs"
                                                                                        />
                                                                                        <span className="font-bold text-sm">
                                                                                            {formatCurrency(charge.charge_amount)}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="text-xs text-gray-600">
                                                                                        Ref: {charge.charge_reference_id}
                                                                                    </div>
                                                                                    {charge.description && (
                                                                                        <div className="text-xs text-gray-500 mt-1 italic">
                                                                                            {charge.description}
                                                                                        </div>
                                                                                    )}
                                                                                    <div className="text-xs text-gray-500 mt-1">
                                                                                        Applied: {formatDate(charge.applied_date)} by {charge.applied_by}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="bg-white p-3 rounded text-center text-sm text-gray-500">
                                                                            No charges applied
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default ArchivedRemittances;
